/**
 * useInvoicePrint — every way the web till prints an invoice.
 *
 *   • print80mm   → the counter receipt as a roll-width page, browser print dialog
 *   • printSystem → the A4 invoice, browser print dialog
 *   • savePdfA4   → the A4 invoice, browser print dialog ("Save as PDF")
 *   • printDefault→ whichever of the two the shop chose (Settings → Invoice)
 *
 * Desktop's print80mm sends ESC/POS over TCP. No browser can open a socket,
 * so that path failed on every click here; the receipt now prints as a page
 * through whatever receipt printer the OS has a driver for.
 *
 * Settings are read synchronously from useSettingsStore so the hook
 * stays cheap to call from row-action menus without re-renders.
 */

import { useCallback, useState } from 'react';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useAppStore } from '@/store/useAppStore';
import { database } from '@/db';
import Merchant from '@/db/models/Merchant';
import { printer, pdf } from '@/platform';
import { invoiceToReceipt } from './adapters/invoiceToReceipt';
import { buildReceipt } from './engine/ReceiptBuilder';
import { resolveReceiptConfig } from './engine/receiptConfig';
import { buildReceiptHtml } from './templates/receiptHtml';
import { buildA4Html, resolveA4TemplateId, A4_TEMPLATE_SETTING, A4_COLUMNS_SETTING } from './templates/a4Templates';
import { parseA4Columns } from './templates/a4Columns';
import { BILL_DOCUMENT_SETTING, resolveBillDocument } from './billDocument';
import type { InvoiceCopyType } from './contracts/ReceiptContracts';
import type { PaperWidth, PrintResult } from './contracts/PrintTypes';

export function useInvoicePrint() {
  const [isPrinting, setIsPrinting] = useState(false);

  /** The counter receipt, at the roll width chosen in Settings → Invoice. */
  const print80mm = useCallback(async (invoiceId: string): Promise<PrintResult> => {
    if (isPrinting) return { ok: false, transport: 'system', errorCode: 'cancelled', message: 'A print job is already running' };
    const s = useSettingsStore.getState();
    const rawWidth = s.getNumber('THERMAL_PRINTER_PAPER_WIDTH');
    const paperWidth = (rawWidth === 58 || rawWidth === 112 ? rawWidth : 80) as PaperWidth;
    const config = resolveReceiptConfig(s);

    setIsPrinting(true);
    try {
      const data = await invoiceToReceipt(invoiceId);
      let logo: string | null = null;
      if (s.getBoolean('INVOICE_SHOW_LOGO')) {
        const merchantId = useAppStore.getState().currentUser?.merchantId;
        if (merchantId) {
          try {
            logo = (await database.get<Merchant>('merchants').find(merchantId)).logoUrl ?? null;
          } catch {
            /* no logo available — print without */
          }
        }
      }
      const { text } = buildReceipt(data, { paperWidth, autoCut: false }, config);
      const res = await printer.printHtml({ html: buildReceiptHtml(text, paperWidth, logo) });
      return { ok: res.ok, transport: 'system', errorCode: res.ok ? undefined : 'unknown', message: res.ok ? 'Sent to printer' : res.message || 'Print failed' };
    } catch (err) {
      return { ok: false, transport: 'system', errorCode: 'unknown', message: (err as Error).message };
    } finally {
      setIsPrinting(false);
    }
  }, [isPrinting]);

  /**
   * Print to an installed OS printer (USB / parallel / dot-matrix / laser) via
   * the Windows driver — this is how TVS/Epson impact printers, which don't
   * speak ESC/POS-over-TCP, are supported. Renders the A4 invoice HTML and
   * prints silently to the printer chosen in Settings ('' = system default).
   */
  const printSystem = useCallback(async (invoiceId: string, copyType?: InvoiceCopyType): Promise<PrintResult> => {
    if (isPrinting) return { ok: false, transport: 'system', errorCode: 'cancelled', message: 'A print job is already running' };
    const s = useSettingsStore.getState();
    const deviceName = s.getSetting('SYSTEM_PRINTER_NAME');
    setIsPrinting(true);
    try {
      const data = await invoiceToReceipt(invoiceId);
      const html = buildA4Html(data, resolveA4TemplateId(s.getSetting(A4_TEMPLATE_SETTING)), {
        copyType,
        columns: parseA4Columns(s.getSetting(A4_COLUMNS_SETTING)),
      });
      const res = await printer.printHtml({ html, deviceName: deviceName || undefined });
      return {
        ok: res.ok,
        transport: 'system',
        errorCode: res.ok ? undefined : 'unknown',
        message: res.ok ? 'Sent to printer' : res.message || 'Print failed',
      };
    } catch (err) {
      return { ok: false, transport: 'system', errorCode: 'unknown', message: (err as Error).message };
    } finally {
      setIsPrinting(false);
    }
  }, [isPrinting]);

  /** A4 PDF via Chromium's printToPDF. Returns the saved path on success. */
  const savePdfA4 = useCallback(async (invoiceId: string, copyType?: InvoiceCopyType): Promise<{ ok: boolean; path?: string; canceled?: boolean; message?: string }> => {
    if (isPrinting) return { ok: false, message: 'A print job is already running' };
    setIsPrinting(true);
    try {
      const data = await invoiceToReceipt(invoiceId);
      const s = useSettingsStore.getState();
      const templateId = resolveA4TemplateId(s.getSetting(A4_TEMPLATE_SETTING));
      const html = buildA4Html(data, templateId, {
        copyType,
        columns: parseA4Columns(s.getSetting(A4_COLUMNS_SETTING)),
      });
      const safeName = data.invoiceNumber.replace(/[^A-Za-z0-9_\-]+/g, '_');
      // The copy marking goes in the filename too, so three saved copies of one
      // invoice do not overwrite each other.
      const suffix = copyType && copyType !== 'ORIGINAL' ? `_${copyType.toLowerCase()}` : '';
      return await pdf.saveFromHtml({ html, defaultFileName: `${safeName}${suffix}.pdf` });
    } catch (err) {
      return { ok: false, message: (err as Error).message };
    } finally {
      setIsPrinting(false);
    }
  }, [isPrinting]);

  /** The shop's own bill — receipt or A4, as chosen in Settings → Invoice. */
  const printDefault = useCallback(async (invoiceId: string): Promise<PrintResult> => {
    const doc = resolveBillDocument(useSettingsStore.getState().getSetting(BILL_DOCUMENT_SETTING));
    return doc === 'a4' ? printSystem(invoiceId) : print80mm(invoiceId);
  }, [print80mm, printSystem]);

  return { isPrinting, print80mm, printSystem, savePdfA4, printDefault };
}
